<script setup lang="ts">
import { computed, ref, toRef, watch } from 'vue';
import Core from '@/core/core/core';
import { TopPopup, TopPopupListItem, TopPopupWidgetInput } from '@/components/popup/popup';
import { TopPreloader } from '@/components/forms/forms';
import type { Item, Props, Slots } from './types';
import Selector2ItemMulti from './itemMulti.vue';
import { useAPI } from './composables/useAPI';
import { useMenu } from './composables/useMenu';
import type { TopLibPopup } from '../../popup/lib/popup';

const props = withDefaults(defineProps<Props>(), {
	items: () => [] as Item[],
	size: 's',
	minLength: 0,
	searchType: 'popup',
});

const model = defineModel<Props['modelValue']>({ required: true });

defineSlots<Slots>();

const searchTypeLocal = computed(() => {
	// multiselect не поддерживает inline ввода
	if (props.searchType === 'inline' && props.multiselect) return 'popup';

	// В мобильной версии popup работает в полноэкранном режиме
	if (props.searchType === 'inline' && Core.state.isMobile) return 'popup';

	return props.searchType;
});

let minLength = 0;
if (searchTypeLocal.value === 'inline' && props.minLength) {
	minLength = props.minLength;
}

/**
 * Объект для работы с API
 */
const api = useAPI(props.api, props.apiSetSearchParams, minLength, props.useCache);

/**
 * Объект для работы с меню
 */
const menu = useMenu(
	model,
	toRef(props, 'items'),
	props.multiselect,
	toRef(props, 'appendAllValue'),
	toRef(props, 'appendSearchToResult'),
	searchTypeLocal,
	minLength,
	api,
);

const component = computed(() => {
	if (props.buttonProps) return 'TopButton';
	if (searchTypeLocal.value === 'inline') return 'TopInput';

	return 'div';
});

/**
 * Экземпляр компонента Popup
 */
const popupRef = ref<any>(null);

/**
 * Получить доступ к объекту popup
 */
const getPopup = (): TopLibPopup | undefined => {
	return popupRef.value?.popup;
};

if (api.apiRequest) {
	// отложенный поиск при вводе текста
	watch(menu.searchText, () => {
		api.setSearchTextAndLoad(menu.searchText.value);

		if (searchTypeLocal.value === 'inline') {
			if (menu.genIsShort()) {
				getPopup()?.close();
			}
		}
	});
}

watch(model, () => {
	/**
	 * При изменении значения модели в режиме inline необходимо очистить поле поиска,
	 * так как поиск больше не актуален после выбора значения
	 */
	if (searchTypeLocal.value === 'inline') {
		menu.resetSearch();
	}
});

const placeholder = computed(() => {
	if (Array.isArray(model.value) || props.multiselect || !props.selectedAsPlaceholder && searchTypeLocal.value !== 'inline') {
		return props.placeholder;
	}

	return model.value?.name || props.placeholder;
});

/**
 * Обработчик ввода клавиш на кнопке или поле ввода
 */
const onOpenerKeydown = (e: KeyboardEvent) => {
	let needOpen = false;

	switch (e.key) {
		case 'Delete':
		case 'Backspace':
			if (Array.isArray(model.value)) {
				e.preventDefault();
				e.stopPropagation();

				model.value.pop();
			}

			break;
		case 'ArrowUp':
		case 'ArrowRight':
		case 'ArrowDown':
		case 'ArrowLeft':
		case 'Enter':
		case ' ':
			needOpen = true;

			break;
		case 'Escape':
			if (searchTypeLocal.value === 'inline') {
				// очистка введенного текста
				menu.resetSearch();
			}

			break;
	}

	// введен символ
	const symbolPressed = e.key.length === 1 && !e.ctrlKey && !e.metaKey;

	// введен символ
	if (searchTypeLocal.value === 'popup' || searchTypeLocal.value === 'inline') {
		if (symbolPressed) {
			needOpen = true;
		}
	}

	// popup уже открыт
	if (getPopup()?.elPopup) {
		needOpen = false;
	}

	if (needOpen) {
		if (searchTypeLocal.value === 'popup') {
			e.preventDefault();
			e.stopPropagation();

			// введен символ
			if (symbolPressed) {
				// начало ввода, сразу ввести первый символ вместе с открытием popup
				if (!menu.searchText.value) {
					menu.searchText.value = e.key;
				}
			}
		}

		(e.currentTarget as HTMLElement)?.click();
	}
};

let disabledFocusToOpen = false;

const onFocus = (e: FocusEvent) => {
	if (disabledFocusToOpen) return;

	if (searchTypeLocal.value === 'inline') {
		(e.currentTarget as HTMLElement)?.click();
	}
};

const onClickChanger = (e: MouseEvent) => {
	e.preventDefault();

	disabledFocusToOpen = true;
	(e.currentTarget as HTMLElement)?.parentElement?.focus();
	disabledFocusToOpen = false;

	menu.selectNextItem();
};

/**
 * Обработчик открытия попапа
 */
const onOpen = () => {
	disabledFocusToOpen = true;

	if (api.apiRequest) {
		if (menu.genIsShort()) {
			getPopup()?.close();

			return;
		}

		// при открытии сразу выполнить поиск
		api.setSearchTextAndLoad(menu.searchText.value, false);
	}
};

/**
 * Обработчик закрытия попапа
 */
const onClose = () => {
	setTimeout(() => {
		disabledFocusToOpen = false;
	}, 100);

	// очистка введенного текста
	if (searchTypeLocal.value === 'popup') {
		if (menu.searchText.value) menu.resetSearch();
	}
};

/**
 * Обработчик прокрутки списка контента
 *
 * Для дозагрузки элеменов через api
 *
 * @param {Event} e - Событие прокрутки
 */
const onScrollContentList = (e: Event) => {
	const el = e.target as HTMLElement;

	if (el.scrollTop / (el.scrollHeight - el.offsetHeight) > 0.8) {
		api.load(true);
	}
};

// для storybook
if ((window as any).__STORYBOOK_PREVIEW__) {
	watch(
		() => props.multiselect,
		() => {
			if (props.multiselect) {
				if (!Array.isArray(model.value)) {
					model.value = [
						{
							id: 1,
							name: 'Выбери меня',
						},
					];
				}
			} else {
				if (Array.isArray(model.value)) {
					model.value = {
						id: null,
						name: '',
					};
				}
			}
		},
		{ immediate: true },
	);

	watch(
		() => props.searchType,
		() => {
			// пауза для установки props в адресной строке
			setTimeout(() => {
				location.reload();
			}, 500);
		},
	);
}
</script>

<template>
	<TopPopup
		ref="popupRef"
		@open="onOpen()"
		@close="onClose()"
		@scrollContentList="api ? onScrollContentList($event) : undefined"
		:notch="searchTypeLocal !== 'inline'"
		:transitionDuration="0"
	>
		<template #opener>
			<component
				:is="component"

				:="buttonProps"

				ref="subcomponentSelector"
				:class="{
					'top-selector2' : true,
					'top-selector2-multiselect': multiselect,
					['top-selector2-' + modificator]: !!modificator,
					'top-as-input': !buttonProps,
					'top-as-selector': true,
					['top-size_' + size]: true,
					['top-disabled']: disabled,
					['top-forms-focusable']: !disabled,
					['top-error']: isError,
				}"
				tabindex="0"
				@keydown="onOpenerKeydown"
				v-top-focus.onupdate="isError"

				:placeholder
				v-model="menu.searchText.value"
				@focus="onFocus"
				@blur="getPopup()?.isClosed ? menu.resetSearch() : ''"
			>
				<template v-if="multiselect">
					<div class="top-selector2_activeItems">
						<Selector2ItemMulti
							v-for="item of model as Item[]"
							:id="item.id"
							:name="item.name"
							@delete="menu.deleteItemById"
						/>
					</div>
				</template>

				<span v-if="searchTypeLocal !== 'inline' && !multiselect" class="top-selector2_activeName top-ellipsis">
					{{ !Array.isArray(model) ? model.name : '' }}
				</span>

				<span v-if="multiselect && !model.length" class="top-selector2_placeholder top-ellipsis">
					{{ placeholder }}
				</span>

				<span
					v-if="addChanger && !buttonProps && !multiselect && menu.items.value.length > 1 && !disabled"
					class="top-changer top-changer-selector top-popup_widget"
					data-top-popup-disabled="true"
					@click="onClickChanger"
				></span>
			</component>
		</template>

		<template #widget v-if="searchTypeLocal === 'popup'">
			<TopPopupWidgetInput
				title="Поиск"
				icon=""
				v-model="menu.searchText.value"
				:isLoading="api.isLoading.value && !api.apiRequest?.params.offset"
				:placeholder
			/>
		</template>

		<template #contentList>
			<TopPopupListItem
				v-for="item of menu.itemsForShow.value"
				:class="{
					'top-active': !Array.isArray(model) && !multiselect && model.id === item.id && model.name === item.name,
				}"
				:key="item.id ?? undefined"
				:closeByClick="!multiselect || Core.state.isMobile"
				@click="menu.selectItem(item)"
			>
				<slot
					v-if="$slots.item"
					name="item"
					:item
				></slot>

				<template
					v-else
				>
					{{ item.name }}
				</template>
			</TopPopupListItem>

			<TopPopupListItem
				v-if="!api.isLoading.value && !menu.itemsForShow.value.length"
				type="regular"
			>
				{{ $i18n.Common.No_results }}
			</TopPopupListItem>

			<!-- Индикатор загрузки, первичная дозагрузка отображается в поле поиска, если оно отображено -->
			<TopPopupListItem
				v-if="api.isLoading.value && (searchType !== 'popup' || api.apiRequest?.params.offset)"
				type="regular"
			>
				<TopPreloader type="circles"/>
			</TopPopupListItem>
		</template>
	</TopPopup>
</template>

<style>
.top-selector2 {
	width: 180px;
	padding: var(--top-padding-1) var(--top-forms-padding);
}

.top-selector2::placeholder,
.top-selector2_placeholder {
	color: var(--color-text-2);
}

.top-selector2_placeholder {
	padding-left: var(--top-padding-1);
}

.top-selector2-multiselect {
	width: auto;
	padding: var(--top-padding-1);
	flex-grow: 1;
}

.top-selector2.top-active {
	--top-forms-border-color: var(--top-forms-border-color-hover);
}

.top-selector2_activeItems {
	display: flex;
	flex-wrap: wrap;
	gap: var(--top-padding-1);
	max-width: 100%;
}

.top-selector2_activeName {
	white-space: nowrap;
}

.top-changer-selector {
	width: auto;
	padding: var(--top-padding-1);
	margin: calc(0px - var(--top-padding-1));
	transform: translateX(0);
}

.top-selector2.top-button {
	justify-content: start;
}

.top-selector2.top-input_input ~ .top-changer-selector {
	transform: translateX(-52px);
}

.top-selector2.top-as-selector {
	padding-right: calc(var(--top-selector-arrow-width) + var(--top-padding-2));
}
</style>
